import { useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ClientItem } from '../services/clientService';
import { getAllClients } from '../services/clientService';
import {
  getBesoinsByClient,
  getOrCreateClient2026AsWasBaseline,
  type BesoinData,
} from '../services/besoinsService';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import { QUALITY_LEVELS } from '../data/gestionDocumentaireTypes';
import { generateManagedDocumentWithAI } from '../services/openaiService';
import { createDocument, finalizeClient2026AnalysisDocument } from '../services/documentsService';
import { getIntakeTemplatesByDocumentType } from '../domain/besoins/templates';
import { buildKmiContextMarkdown, computeKmiPillarScores } from '../domain/besoins/kmi';
import {
  applyClient2026AsWasBaseline,
  buildClient2026DocumentRows,
  buildClient2026MaturityProfile,
  CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_AXE_TITLES,
  CLIENT_2026_ROWS,
  escapeClient2026MarkdownCell,
  getClient2026ColumnValue,
  isCurrentClient2026Source,
  mergeClient2026PhaseResponses,
  resolveClient2026Schema,
} from '../domain/besoins/templates/prise-de-besoin-client-2026';
import {
  CLIENT_2026_ROWS as CLIENT_2026_V3_ROWS,
  applyClient2026AsWasBaseline as applyClient2026V3AsWasBaseline,
  buildClient2026DocumentRows as buildClient2026V3DocumentRows,
  buildClient2026MaturityProfile as buildClient2026V3MaturityProfile,
  getClient2026ColumnValue as getClient2026V3ColumnValue,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-v3';
import {
  CLIENT_2026_ALL_COL_DEFS as LEGACY_CLIENT_2026_ALL_COL_DEFS,
  CLIENT_2026_AXE_TITLES as LEGACY_CLIENT_2026_AXE_TITLES,
  CLIENT_2026_ROWS as LEGACY_CLIENT_2026_ROWS,
  buildLegacyClient2026DocumentRows,
  selectLegacyClient2026DocumentColumns,
} from '../domain/besoins/templates/prise-de-besoin-client-2026-legacy';
import GenerationProgress from './GenerationProgress';

interface DocumentCreationWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTypeCode: string;
  onCreated?: (documentId: number) => void;
}

type Step = 1 | 2 | 3;

const EBAUCHE_ODS_SOURCE_TEMPLATES = [
  { key: 'ods', templateId: 'ods-support-gestion', label: 'Prise de besoin - ODS' },
  { key: 'client2026', templateId: 'prise-de-besoin-client-2026', label: 'Prise de besoin - Maturité et besoins client' },
  { key: 'kmi', templateId: 'prisme-kmi-v1', label: 'Prisme - KMI' },
] as const;

const PLAN_DIRECTEUR_5_ANS_V2_SOURCE_TEMPLATES = [
  { key: 'kmi', templateId: 'prisme-kmi-v1', label: 'Prisme - KMI' },
  { key: 'client2026', templateId: 'prise-de-besoin-client-2026', label: 'Prise de besoin - Maturité et besoins client' },
] as const;

const getBesoinTime = (besoin: any) => {
  const raw = besoin?.updated_at || besoin?.created_at;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? t : Number(besoin?.id || 0);
};

const getLatestBesoinForTemplate = (items: BesoinData[], templateId: string) =>
  items
    .filter((b: any) => b.template_id === templateId && !b.archived)
    .sort((a: any, b: any) => getBesoinTime(b) - getBesoinTime(a))[0] || null;

const CURRENT_CLIENT_2026_OUTPUT_TYPES = new Set([
  'PRISE_BESOIN_CLIENT_2026',
  'PRISE_BESOIN_CLIENT_2026_ANALYSE',
]);

const isSupportedClient2026Source = (source: Record<string, any> | null | undefined) =>
  (source?.metadata?.client2026_schema === 2 && source?.template_version === '3.0.0')
  || isCurrentClient2026Source({
    schema: source?.metadata?.client2026_schema,
    templateVersion: String(source?.template_version || ''),
  });

export default function DocumentCreationWizardModal({
  isOpen,
  onClose,
  documentTypeCode,
  onCreated,
}: DocumentCreationWizardModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');

  const [besoins, setBesoins] = useState<BesoinData[]>([]);
  const [isLoadingBesoins, setIsLoadingBesoins] = useState(false);
  const [selectedBesoinId, setSelectedBesoinId] = useState<number | null>(null);

  const [qualityLevel, setQualityLevel] = useState<number>(3);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  const documentType = useMemo(
    () => DOCUMENT_TYPES.find((t) => t.code === documentTypeCode),
    [documentTypeCode]
  );
  const requiresCurrentClient2026Schema = CURRENT_CLIENT_2026_OUTPUT_TYPES.has(documentTypeCode);

  const isEbaucheOds = documentTypeCode === 'EBAUCHE_ODS';
  const isPlanDirecteur5AnsV2 = documentTypeCode === 'PLAN_DIRECTEUR_5_ANS_V2';

  const ebaucheOdsSources = useMemo(() => {
    if (!isEbaucheOds) return [];
    return EBAUCHE_ODS_SOURCE_TEMPLATES.map((source) => ({
      ...source,
      besoin: getLatestBesoinForTemplate(besoins, source.templateId),
    }));
  }, [besoins, isEbaucheOds]);

  const missingEbaucheOdsSources = ebaucheOdsSources.filter((source) => !source.besoin);

  const planDirecteur5AnsV2Sources = useMemo(() => {
    if (!isPlanDirecteur5AnsV2) return [];
    return PLAN_DIRECTEUR_5_ANS_V2_SOURCE_TEMPLATES.map((source) => ({
      ...source,
      besoin: getLatestBesoinForTemplate(besoins, source.templateId),
    }));
  }, [besoins, isPlanDirecteur5AnsV2]);

  const missingPlanDirecteur5AnsV2Sources = planDirecteur5AnsV2Sources.filter((source) => !source.besoin);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedClientId('');
    setSelectedBesoinId(null);
    setQualityLevel(3);
    setError(null);

    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const list = await getAllClients();
        const active = list
          .filter((c) => c.actif !== false)
          .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }));
        setClients(active);
      } catch (e: any) {
        console.error('Erreur chargement clients pour création de document:', e);
        setError("Erreur lors du chargement des clients");
      } finally {
        setIsLoadingClients(false);
      }
    };

    loadClients();
  }, [isOpen]);

  useEffect(() => {
    const loadBesoins = async () => {
      if (!selectedClientId || typeof selectedClientId !== 'number') {
        setBesoins([]);
        setSelectedBesoinId(null);
        return;
      }
      setIsLoadingBesoins(true);
      setError(null);
      try {
        const all = await getBesoinsByClient(selectedClientId);
        
        // Récupérer les templates compatibles avec le type de document
        const compatibleTemplates = getIntakeTemplatesByDocumentType(documentTypeCode);
        const compatibleTemplateIds = new Set(compatibleTemplates.map(t => t.id));
        
        // Filtrer : non archivées ET compatibles avec le type de document
        const filtered = all.filter((b: any) => {
          if (b.archived) return false;

          if (requiresCurrentClient2026Schema) {
            if (b.template_id !== 'prise-de-besoin-client-2026') return false;
            return isSupportedClient2026Source(b);
          }
          
          // Vérifier si le template de la prise de besoin est compatible avec le type de document
          if (b.template_id && compatibleTemplateIds.has(b.template_id)) {
            return true;
          }
          
          // Fallback pour les anciens cas spéciaux (à garder pour compatibilité)
          if (
            documentTypeCode === 'PLAN_DIRECTEUR' ||
            documentTypeCode === 'ROADMAP_24M' ||
            documentTypeCode === 'OBJECTIFS_KPI_TI' ||
            documentTypeCode === 'CAHIER_CHARGE' ||
            documentTypeCode === 'MATRICE_RACI_TI' ||
            documentTypeCode === 'PLAN_BUDGETAIRE_TI_N1' ||
            documentTypeCode === 'PLAN_BUDGETAIRE_5_ANS' ||
            documentTypeCode === 'REGISTRE_AMELIORATION_CONTINUE_TI' ||
            documentTypeCode === 'REGISTRE_RISQUES_TI' ||
            documentTypeCode === 'OFFRE_SERVICE_BZ'
          ) {
            return b.template_id === 'pdti-standard-pme';
          }
          
          return false;
        });
        
        setBesoins(filtered);
        const first = isEbaucheOds
          ? EBAUCHE_ODS_SOURCE_TEMPLATES.map((source) =>
              getLatestBesoinForTemplate(filtered, source.templateId)
            ).find(Boolean)
          : isPlanDirecteur5AnsV2
            ? PLAN_DIRECTEUR_5_ANS_V2_SOURCE_TEMPLATES.map((source) =>
              getLatestBesoinForTemplate(filtered, source.templateId)
            ).find(Boolean)
          : requiresCurrentClient2026Schema
            ? getLatestBesoinForTemplate(filtered, 'prise-de-besoin-client-2026')
            : filtered[0];
        setSelectedBesoinId(first && typeof first.id === 'number' ? first.id : null);
      } catch (e: any) {
        console.error('Erreur chargement besoins pour création de document:', e);
        setError("Erreur lors du chargement des prises de besoin");
      } finally {
        setIsLoadingBesoins(false);
      }
    };

    if (isOpen) {
      loadBesoins();
    }
  }, [isOpen, selectedClientId, documentTypeCode, isEbaucheOds, isPlanDirecteur5AnsV2, requiresCurrentClient2026Schema]);

  if (!isOpen || !documentType) return null;

  const goNext = () => {
    if (step === 1) {
      if (!selectedClientId || typeof selectedClientId !== 'number') {
        setError('Veuillez sélectionner un client');
        return;
      }
    }
    if (step === 2) {
      if (isEbaucheOds || isPlanDirecteur5AnsV2) {
        const missingSources = isEbaucheOds
          ? missingEbaucheOdsSources
          : missingPlanDirecteur5AnsV2Sources;
        const documentLabel = isEbaucheOds ? "l'Ébauche d'ODS" : 'le Plan Directeur 5 ans V2';
        if (missingSources.length > 0) {
          setError(
            `Sources manquantes pour ${documentLabel}: ${missingSources
              .map((source) => source.label)
              .join(', ')}`
          );
          return;
        }
      } else if (!selectedBesoinId) {
        setError('Veuillez sélectionner une prise de besoin');
        return;
      }
    }
    setError(null);
    setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
  };

  const goPrev = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));

  const handleCreate = async () => {
    if (!selectedClientId || typeof selectedClientId !== 'number') {
      setError('Client requis');
      return;
    }
    if ((isEbaucheOds || isPlanDirecteur5AnsV2) && (isEbaucheOds ? missingEbaucheOdsSources : missingPlanDirecteur5AnsV2Sources).length > 0) {
      const missingSources = isEbaucheOds
        ? missingEbaucheOdsSources
        : missingPlanDirecteur5AnsV2Sources;
      const documentLabel = isEbaucheOds ? "l'Ébauche d'ODS" : 'le Plan Directeur 5 ans V2';
      setError(
        `Sources manquantes pour ${documentLabel}: ${missingSources
          .map((source) => source.label)
          .join(', ')}`
      );
      return;
    }
    if (!isEbaucheOds && !isPlanDirecteur5AnsV2 && !selectedBesoinId) {
      setError('Prise de besoin requise');
      return;
    }
    const besoin = isEbaucheOds
      ? ebaucheOdsSources.find((source) => source.key === 'ods')?.besoin || ebaucheOdsSources[0]?.besoin
      : isPlanDirecteur5AnsV2
        ? planDirecteur5AnsV2Sources.find((source) => source.key === 'client2026')?.besoin || planDirecteur5AnsV2Sources[0]?.besoin
      : besoins.find((b: any) => b.id === selectedBesoinId);
    if (!besoin) {
      setError('Prise de besoin introuvable');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStep('Analyse de la prise de besoin...');
    setError(null);

    // Animation de progression inspirée de la simulation Plan Directeur
    const steps = [
      { progress: 10, text: 'Analyse du contexte et des objectifs...', delay: 800 },
      { progress: 30, text: 'Cartographie des besoins techniques et des contraintes...', delay: 1200 },
      { progress: 55, text: 'Construction de la structure du document (sections ITIL / COBIT)...', delay: 1600 },
      { progress: 78, text: 'Rédaction détaillée par l’IA (ton exécutif, recommandations)...', delay: 2000 },
      { progress: 92, text: 'Finition, cohérence globale et préparation du rendu...', delay: 1600 },
    ];

    let stepIndex = 0;
    const intervalId = window.setInterval(() => {
      if (stepIndex < steps.length) {
        const stepDef = steps[stepIndex];
        setGenerationStep(stepDef.text);
        setGenerationProgress(stepDef.progress);
        stepIndex++;
      }
    }, 1800);

    try {
      // Construire un contexte narratif structuré à partir des phases
      const p1: any = (besoin as any).phase1_data || {};
      const p2: any = (besoin as any).phase2_data || {};
      const p3: any = (besoin as any).phase3_data || {};
      const p4: any = (besoin as any).phase4_data || {};
      const p5: any = (besoin as any).phase5_data || {};
      const p6: any = (besoin as any).phase6_data || {};
      const p7: any = (besoin as any).phase7_data || {};
      const p8: any = (besoin as any).phase8_data || {};
      const p9: any = (besoin as any).phase9_data || {};
      const pm: any = (besoin as any).phase5_metier_data || {};
      const isLegacyClient2026Source = (source: Record<string, any> | null | undefined) =>
        resolveClient2026Schema({
          schema: source?.metadata?.client2026_schema,
          templateVersion: String(source?.template_version || ''),
          isExisting: true,
        }) === 1;
      const client2026SourceSchema = resolveClient2026Schema({
        schema: (besoin as any)?.metadata?.client2026_schema,
        templateVersion: String((besoin as any)?.template_version || ''),
        isExisting: true,
      });
      const isLegacyClient2026Besoin = isLegacyClient2026Source(besoin as any);
      const isCurrentClient2026Besoin = isSupportedClient2026Source(besoin as any);

      if (requiresCurrentClient2026Schema && !isCurrentClient2026Besoin) {
        throw new Error('Le livrable doit être généré depuis une prise de besoin au schéma 2 / version 3.0.0 ou au schéma 3 / version 3.1.0.');
      }

      let context: string;

      const formatPhaseData = (phaseData: any, phaseName: string) => {
        if (!phaseData || Object.keys(phaseData).length === 0) {
          return `${phaseName}:\n- Aucune donnée disponible`;
        }

        const entries = Object.entries(phaseData)
          .filter(([_, value]) => value !== null && value !== undefined && value !== '')
          .map(([key, value]) => {
            const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
            const formattedValue = Array.isArray(value)
              ? value.join(', ')
              : typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : String(value);
            return `- ${label}: ${formattedValue}`;
          });

        return entries.length > 0 ? `${phaseName}:\n${entries.join('\n')}` : `${phaseName}:\n- Aucune donnée disponible`;
      };

      const getClient2026QuestionId = (def: { id: string }) => def.id;

      const client2026HasUsableData = (sourceBesoin: Record<string, unknown> = besoin as any) => {
        const sourceResponses = mergeClient2026PhaseResponses(sourceBesoin);
        if (isLegacyClient2026Source(sourceBesoin as any)) {
          return Object.entries(sourceResponses).some(([key, value]) =>
            key.startsWith('client2026_a') && !key.endsWith('__enabledCols') && String(value ?? '').trim() !== '',
          );
        }
        const candidateSchema = resolveClient2026Schema({
          schema: (sourceBesoin as any)?.metadata?.client2026_schema,
          templateVersion: String((sourceBesoin as any)?.template_version || ''),
          isExisting: true,
        });
        const candidateRows = candidateSchema === 2 ? CLIENT_2026_V3_ROWS : CLIENT_2026_ROWS;
        return candidateRows.some((def) => {
          const qid = getClient2026QuestionId(def);
          return CLIENT_2026_ALL_COL_DEFS.some((col) => {
            if (!col.responseKey || (!def.hasMaturity && ['D', 'E', 'F'].includes(col.key))) return false;
            const value = String(sourceResponses[`${qid}__${col.responseKey}`] ?? '').trim();
            return value !== '' && value !== '-';
          });
        });
      };

      const buildClient2026StrategicContext = (
        clientName: string,
        sourceBesoin: Record<string, unknown> = besoin as any,
      ) => {
        const sourceResponses = mergeClient2026PhaseResponses(sourceBesoin);
        const sourceSchema = resolveClient2026Schema({
          schema: (sourceBesoin as any)?.metadata?.client2026_schema,
          templateVersion: String((sourceBesoin as any)?.template_version || ''),
          isExisting: true,
        });
        const rowsForSource = sourceSchema === 2 ? CLIENT_2026_V3_ROWS : CLIENT_2026_ROWS;
        const readColumn = sourceSchema === 2 ? getClient2026V3ColumnValue : getClient2026ColumnValue;
        const extraCols = CLIENT_2026_ALL_COL_DEFS.filter((col) => col.responseKey);
        const blocks: string[] = [
          '=== DONNÉES DE LA PRISE DE BESOIN CLIENT 2026 - PLAN DIRECTEUR TI ===',
          `Client: ${clientName}`,
          '',
          'INSTRUCTIONS DE LECTURE:',
          '- Utiliser les réponses du client, priorités client/BZ, objectifs SMART, solutions, coûts, contraintes, risques et mitigations comme source du Plan Directeur TI.',
          '- Structurer la vision stratégique autour des piliers BZ fournis dans le formulaire.',
          '- Ne pas inventer de faits, budgets, échéanciers, technologies ou volumes non indiqués.',
          '- Si une donnée est absente, indiquer « Non précisé » ou formuler une hypothèse à valider.',
          '',
        ];

        if (isLegacyClient2026Source(sourceBesoin as any)) {
          Array.from(new Set(LEGACY_CLIENT_2026_ROWS.map((row) => row.axe)))
            .sort((a, b) => a - b)
            .forEach((axe) => {
              blocks.push(`${axe <= 6 ? `PILIER ${axe}` : axe === 7 ? 'SOUS-CATÉGORIE HISTORIQUE DU PILIER 6' : 'AUTRES — SECTION HISTORIQUE'}: ${LEGACY_CLIENT_2026_AXE_TITLES[axe] || ''}`, '---');
              LEGACY_CLIENT_2026_ROWS.filter((row) => row.axe === axe).forEach((def) => {
                const idxInAxe = LEGACY_CLIENT_2026_ROWS.filter((row) => row.axe === axe).indexOf(def) + 1;
                const qid = `client2026_a${axe}_${String(idxInAxe).padStart(2, '0')}`;
                blocks.push(`Question: ${def.question}`);
                if (def.example) blocks.push(`Exemples de référence: ${def.example}`);
                LEGACY_CLIENT_2026_ALL_COL_DEFS
                  .filter((column) => !['A', 'C', 'D', 'E'].includes(column.key))
                  .forEach((column) => {
                    const value = String(sourceResponses[`${qid}__${column.key}`] ?? '').trim();
                    if (value && value !== '-') blocks.push(`${column.label}: ${value}`);
                  });
                blocks.push('');
              });
            });
          blocks.push('=== FIN DES DONNÉES HISTORIQUES DE PRISE DE BESOIN CLIENT ===');
          return blocks.join('\n');
        }

        Array.from(new Set(rowsForSource.map((row) => row.axe)))
          .sort((a, b) => a - b)
          .forEach((axe) => {
            blocks.push(`PILIER ${axe}: ${CLIENT_2026_AXE_TITLES[axe] || ''}`);
            blocks.push('---');
            rowsForSource.filter((row) => row.axe === axe).forEach((def) => {
              blocks.push(`Question: ${def.question}`);
              if (def.example) blocks.push(`Exemples de référence: ${def.example}`);
              extraCols.forEach((col) => {
                const value = readColumn(def, col, sourceResponses);
                if (value && value !== '-') {
                  blocks.push(`${col.label}: ${value}`);
                }
              });
              blocks.push('');
            });
            blocks.push('');
          });

        blocks.push('=== FIN DES DONNÉES DE MATURITÉ ET DE PRISE DE BESOIN CLIENT ===');
        return blocks.join('\n');
      };

      // Construction du contexte selon le type de document
      if (documentType.code === 'PLAN_DIRECTEUR_5_ANS_V2') {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientName = selectedClient?.nom || 'Client';
        const kmiBesoin = planDirecteur5AnsV2Sources.find((source) => source.key === 'kmi')?.besoin;
        const client2026Besoin = planDirecteur5AnsV2Sources.find((source) => source.key === 'client2026')?.besoin;

        context = [
          '=== PLAN DIRECTEUR 5 ANS V2 - CONTEXTE MULTI-FORMULAIRES ===',
          `Client: ${clientName}`,
          '',
          'CADRE RÉFÉRENTIEL BZ 6 + 2 À UTILISER:',
          'Piliers:',
          '1. Gouvernance, stratégie & organisation',
          '2. Architecture, infrastructure & cloud',
          '3. Applications, données & intégrations',
          '4. Services, opérations & expérience utilisateur',
          '5. Cybersécurité, risque & conformité',
          '6. Transformation, innovation & IA',
          'Dimensions transversales:',
          'A. Gestion des actifs & parties prenantes',
          'B. Gestion de projet & amélioration continue',
          '',
          'RÈGLES DE SYNTHÈSE:',
          '- Utiliser Prisme - KMI comme source de maturité actuelle (AS-IS) et de priorisation des écarts.',
          '- Utiliser la Prise de besoin - Maturité et besoins client pour tout le reste du document: contexte, besoins, priorités, objectifs SMART, solutions, coûts, contraintes, risques et mitigations.',
          '- Conserver l’architecture de qualité du Plan Directeur TI, mais avec un horizon strict de 5 ans.',
          '- Garder les références ITIL 4, COBIT 2019 et NIST CSF 2.0 lorsque pertinentes.',
          '- Structurer les constats, la cible, la roadmap, le budget et la gouvernance selon le cadre BZ 6 + 2.',
          '- Ne pas inventer de faits client. Si une information est absente, écrire « Non précisé » ou formuler une hypothèse à valider.',
          '',
          kmiBesoin
            ? buildKmiContextMarkdown({ clientName, besoin: kmiBesoin as any, variant: 'actuelle' })
            : 'SOURCE PRISME - KMI MANQUANTE',
          '',
          client2026Besoin
            ? buildClient2026StrategicContext(clientName, client2026Besoin as any)
            : 'SOURCE PRISE DE BESOIN CLIENT 2026 MANQUANTE',
          '',
          '=== FIN DU CONTEXTE PLAN DIRECTEUR 5 ANS V2 ===',
        ].join('\n');
      } else if (documentType.code === 'EBAUCHE_ODS') {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientName = selectedClient?.nom || 'Client';
        const odsBesoin = ebaucheOdsSources.find((source) => source.key === 'ods')?.besoin;
        const client2026Besoin = ebaucheOdsSources.find((source) => source.key === 'client2026')?.besoin;
        const kmiBesoin = ebaucheOdsSources.find((source) => source.key === 'kmi')?.besoin;

        const formatFullBesoin = (sourceLabel: string, sourceBesoin: any) => {
          const chunks = [
            `=== SOURCE: ${sourceLabel} ===`,
            `Nom: ${sourceBesoin?.nom || sourceBesoin?.title || 'Sans nom'}`,
            `Template: ${sourceBesoin?.template_id || 'Non précisé'}`,
            `Créée le: ${sourceBesoin?.created_at || 'Non précisé'}`,
            `Mise à jour: ${sourceBesoin?.updated_at || 'Non précisé'}`,
          ];

          for (let i = 1; i <= 9; i += 1) {
            const data = sourceBesoin?.[`phase${i}_data`];
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
              chunks.push('', formatPhaseData(data, `PHASE ${i}`));
            }
          }

          const metier = sourceBesoin?.phase5_metier_data;
          if (metier && typeof metier === 'object' && Object.keys(metier).length > 0) {
            chunks.push('', formatPhaseData(metier, 'PAGE MÉTIER SPÉCIFIQUE'));
          }

          return chunks.join('\n');
        };

        context = [
          '=== ÉBAUCHE D’ODS - CONTEXTE MULTI-FORMULAIRES ===',
          `Client: ${clientName}`,
          '',
          'CADRE CLIENT BZ À UTILISER POUR L’ANALYSE:',
          '1. Gouvernance, stratégie & organisation',
          '2. Architecture, infrastructure & cloud',
          '3. Applications, données & intégrations',
          '4. Services, opérations & expérience utilisateur',
          '5. Cybersécurité, risque & conformité',
          '6. Transformation, innovation & IA',
          '',
          'RÈGLES DE SYNTHÈSE:',
          '- Tenir compte des 3 sources ci-dessous ensemble, sans en ignorer une.',
          '- Utiliser la Prise de besoin ODS pour le catalogue, les services, les exclusions, la tarification et les produits additionnels.',
          '- Utiliser la Prise de besoin - Maturité et besoins client pour les enjeux d’affaires, priorités, objectifs SMART, solutions, coûts, risques et mitigations.',
          '- Utiliser Prisme - KMI pour qualifier la maturité actuelle et prioriser les écarts par pilier.',
          '- Ne pas inventer de faits client. Si une information est absente, écrire « Non précisé » ou formuler une hypothèse à valider.',
          '- Produire un document dans l’esprit de l’Offre de service BZ - Solution d’affaire holistique, mais sans bloc visuel mockup.',
          '',
          odsBesoin ? formatFullBesoin('PRISE DE BESOIN - ODS', odsBesoin) : 'SOURCE ODS MANQUANTE',
          '',
          client2026Besoin
            ? formatFullBesoin('PRISE DE BESOIN CLIENT 2026 - ANALYSE & RECOMMANDATIONS IA', client2026Besoin)
            : 'SOURCE CLIENT 2026 MANQUANTE',
          '',
          kmiBesoin
            ? buildKmiContextMarkdown({ clientName, besoin: kmiBesoin as any, variant: 'actuelle' })
            : 'SOURCE PRISME - KMI MANQUANTE',
          '',
          '=== FIN DU CONTEXTE MULTI-FORMULAIRES ===',
        ].join('\n');
      } else if (documentType.code === 'PLAN_BUDGETAIRE_5_ANS') {
        // Contexte depuis la prise de besoin "Prise de besoin - Plan budgétaire 5 ans" (template plan-budgetaire-5ans-v1)
        const p1Plan5: any = (besoin as any).phase1_data || {};
        const plan5Ans: Array<{ id: string; name: string; items: Array<{ id: string; label: string; y2026?: string; y2027?: string; y2028?: string; y2029?: string; y2030?: string }> }> = Array.isArray(p1Plan5.plan_budgetaire_5ans) ? p1Plan5.plan_budgetaire_5ans : [];
        const projetsEnCours = typeof p1Plan5.projets_en_cours === 'string' ? p1Plan5.projets_en_cours : '';
        const ameliorationsFaites = typeof p1Plan5.ameliorations_faites === 'string' ? p1Plan5.ameliorations_faites : '';
        const ameliorationsSuggerees = typeof p1Plan5.ameliorations_suggerees === 'string' ? p1Plan5.ameliorations_suggerees : '';

        const lignesTableau: string[] = [];
        plan5Ans.forEach((cat) => {
          lignesTableau.push(`\n### ${cat.name}`);
          (cat.items || []).forEach((it) => {
            const y2026 = it.y2026 ?? '', y2027 = it.y2027 ?? '', y2028 = it.y2028 ?? '', y2029 = it.y2029 ?? '', y2030 = it.y2030 ?? '';
            if (it.label || y2026 || y2027 || y2028 || y2029 || y2030) {
              lignesTableau.push(`- ${it.label || '(sans libellé)'} | 2026: ${y2026} | 2027: ${y2027} | 2028: ${y2028} | 2029: ${y2029} | 2030: ${y2030}`);
            }
          });
        });

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN BUDGÉTAIRE 5 ANS ===',
          '',
          'TABLEAU BUDGÉTAIRE VISION 5 ANS (par catégorie et poste, montants par année):',
          lignesTableau.length ? lignesTableau.join('\n') : '- Aucune ligne renseignée',
          '',
          'PROJETS EN COURS:',
          projetsEnCours || '- Non renseigné',
          '',
          'AMÉLIORATIONS FAITES:',
          ameliorationsFaites || '- Non renseigné',
          '',
          'AMÉLIORATIONS SUGGÉRÉES:',
          ameliorationsSuggerees || '- Non renseigné',
          '',
          '=== FIN DES DONNÉES - PLAN BUDGÉTAIRE 5 ANS ===',
          '',
          'INSTRUCTIONS CRITIQUES POUR LA GÉNÉRATION DU DOCUMENT:',
          '- Ce document doit être un PLAN BUDGÉTAIRE VISION 5 ANS professionnel, généré à partir UNIQUEMENT des données ci-dessus.',
          '- Bergeron Zaccheo (BZ) est reconnue comme LA référence en plan budgétaire TI et en direction TI : le ton et les recommandations doivent refléter cette expertise (meilleures pratiques, rigueur, alignement stratégique).',
          '- Intégrer OBLIGATOIREMENT le tableau budgétaire 5 ans (par catégorie/poste et par année) dans le document, avec les montants EXACTS fournis. Utiliser des tableaux Markdown clairs.',
          '- Inclure les sections Projets en cours, Améliorations faites et Améliorations suggérées en s\'appuyant sur les textes fournis.',
          '- Le document doit être structuré, orienté décision (DG/DTI), en français, avec montants en dollars canadiens ($) lorsque des chiffres sont présents.',
          '- Ne pas inventer de données : tout doit découler des données de la prise de besoin ci-dessus.',
        ].join('\n');
      } else if (documentType.code === 'POLITIQUE_CYBER') {
        // Contexte spécifique pour Politique de Cybersécurité
        // Les données sont stockées avec les IDs des questions comme clés
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              // Formater la clé pour être plus lisible
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bTi\b/g, 'TI')
                .replace(/\bMfa\b/g, 'MFA')
                .replace(/\b2fa\b/g, '2FA')
                .replace(/\bByod\b/g, 'BYOD')
                .replace(/\bIa\b/g, 'IA');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Qualification & Contexte');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Contrôles d\'Accès & Utilisation');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Authentification & Sécurité');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Politiques & Restrictions');

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - POLITIQUE DE CYBERSÉCURITÉ ===',
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section de la politique.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les règles, comparaisons par groupe, et matrices de décision.',
        ].join('\n');
      } else if (
        documentType.code === 'ODS' ||
        (documentType.code === 'OFFRE_SERVICE_BZ' && (besoin as any).template_id === 'ods-support-gestion')
      ) {
        const formatArray = (value: any) => (Array.isArray(value) ? value.join(', ') : value || 'Non précisé');

        const normalizeHeader = (header: string) =>
          String(header || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        const splitMarkdownRow = (row: string) => {
          const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
          return trimmed.split('|').map((cell) => cell.trim());
        };

        const isMarkdownSeparator = (line: string) =>
          /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line.trim());

        const parseMarkdownTable = (content: string) => {
          if (!content || !content.includes('|')) return null;
          const lines = content.split('\n').map((ln) => ln.trim());
          for (let i = 0; i < lines.length - 1; i += 1) {
            const headerLine = lines[i];
            const separatorLine = lines[i + 1];
            if (!headerLine.includes('|') || !isMarkdownSeparator(separatorLine)) continue;

            const headers = splitMarkdownRow(headerLine).filter((h) => h);
            if (headers.length < 2) continue;

            const rows: string[][] = [];
            for (let j = i + 2; j < lines.length; j += 1) {
              const rowLine = lines[j];
              if (!rowLine || !rowLine.includes('|')) break;
              rows.push(splitMarkdownRow(rowLine));
            }
            return { headers, rows };
          }
          return null;
        };

        const computeMonthlyTotalFromMarkdown = (markdown: string) => {
          const parsed = parseMarkdownTable(markdown);
          if (!parsed) return '';
          const totalIndex = parsed.headers.findIndex((h) => normalizeHeader(h).includes('total'));
          if (totalIndex === -1) return '';
          const sum = parsed.rows.reduce((acc, row) => {
            const raw = String(row?.[totalIndex] || '');
            const cleaned = raw.replace(/\s/g, '').replace('$', '').replace(',', '.');
            const val = parseFloat(cleaned);
            return Number.isFinite(val) ? acc + val : acc;
          }, 0);
          if (!Number.isFinite(sum) || sum <= 0) return '';
          return `${sum.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
        };

        const part1 = [
          '=== ODS - PARTIE 1 : OFFRE COMMERCIALE ===',
          `Nom du client: ${p1.company_name || 'Non précisé'}`,
          `Date de proposition: ${p1.proposal_date || 'Non précisé'}`,
          `Directeur de compte: ${p1.account_manager || 'Non précisé'}`,
          `Ville / lieu: ${p1.proposal_city || 'Non précisé'}`,
          `Résumé du contexte: ${p1.context_summary || 'Non précisé'}`,
          `Problématiques identifiées: ${formatArray(p1.needs_highlights)}`,
          `Recommandations: ${p1.recommendations || 'Non précisé'}`,
          `Plan de travail - Implanter: ${formatArray(p1.plan_implanter)}`,
          `Plan de travail - Gérer: ${formatArray(p1.plan_gerer)}`,
          `Plan de travail - Optimiser: ${formatArray(p1.plan_optimiser)}`,
        ].join('\n');

        const part2 = [
          '=== ODS - PARTIE 2 : CATALOGUE DE SERVICES (TABLEAUX) ===',
          'Tableau - Services distinctifs:',
          p2.catalog_inclusions_distinctifs || 'Non précisé',
          '',
          'Tableau - Services gérés (infogérance):',
          p2.catalog_inclusions_infogerance || 'Non précisé',
          '',
          'Tableau - Infonuagique & sauvegardes:',
          p2.catalog_inclusions_cloud || 'Non précisé',
          '',
          'Tableau - Équipements, logiciels et licences:',
          p2.catalog_inclusions_equip || 'Non précisé',
          '',
          `Notes / ajustements: ${p2.catalog_notes || 'Non précisé'}`,
        ].join('\n');

        const part3 = [
          '=== ODS - PARTIE 3 : TARIFICATION DÉTAILLÉE (TABLEAU) ===',
          'Tableau de tarification:',
          p3.pricing_table || 'Non précisé',
          '',
          `Mensualité TI totale: ${p3.monthly_total || computeMonthlyTotalFromMarkdown(p3.pricing_table) || 'Non précisé'}`,
          `Frais uniques: ${p3.one_time_fees || 'Non précisé'}`,
          `Notes sur la tarification: ${p3.pricing_notes || 'Non précisé'}`,
        ].join('\n');

        let autresProduitsList: Array<{ type: string; title: string; pricingTable: string; migrationFees: string }> = [];
        try {
          const ap = p4.autres_produits;
          if (typeof ap === 'string') autresProduitsList = JSON.parse(ap || '[]');
          else if (Array.isArray(ap)) autresProduitsList = ap;
        } catch {
          autresProduitsList = [];
        }
        const part4Lines = ['=== ODS - PARTIE 4 : AUTRES PRODUITS ==='];
        if (autresProduitsList.length === 0) {
          part4Lines.push('Aucun produit additionnel.');
        } else {
          autresProduitsList.forEach((p: { type: string; title: string; pricingTable: string; migrationFees: string }) => {
            const produitMensualite = computeMonthlyTotalFromMarkdown(p.pricingTable);
            part4Lines.push(
              '',
              `--- ${p.type} ---`,
              p.title ? `Titre: ${p.title}` : 'Titre: Non précisé',
              'Tableau mensualité:',
              p.pricingTable || 'Non précisé',
              produitMensualite ? `Mensualité du produit: ${produitMensualite}` : 'Mensualité du produit: Non précisé',
              `Frais de migration: ${p.migrationFees || 'Non précisé'}`
            );
          });
        }
        const part4 = part4Lines.join('\n');

        const part5 = [
          '=== ODS - PARTIE 5 : ANNEXES PÉDAGOGIQUES ===',
          `Sections d'expertise à inclure: ${formatArray(p5.expertise_sections)}`,
          `Programmes à inclure: ${formatArray(p5.programs_to_include)}`,
          `Notes annexes: ${p5.annexes_notes || 'Non précisé'}`,
        ].join('\n');

        context = [
          documentType.code === 'OFFRE_SERVICE_BZ'
            ? '=== MODE OFFRE_SERVICE_BZ À PARTIR D’UNE PRISE DE BESOIN ODS ===\n- Utiliser les données ODS ci-dessous comme source principale.\n- Transformer ces données en Offre de service BZ - Solution d’affaire holistique.\n- Présenter clairement 3 choix: Essentielle, Recommandée et Complète.\n- Conserver les éléments concrets de catalogue, tarification, exclusions, frais uniques et produits additionnels.'
            : '',
          part1,
          '',
          part2,
          '',
          part3,
          '',
          part4,
          '',
          part5,
        ].filter(Boolean).join('\n');
      } else if (documentType.code === 'RAPPORT_INCIDENT') {
        // Contexte spécifique pour Rapport d'Incident
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bCai\b/g, 'CAI')
                .replace(/\bMttr\b/g, 'MTTR');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Identification & Classification');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Chronologie & Détection');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Causes et impacts');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Réponse & Résolution');
        const phase5 = formatPhaseData(p5, 'PHASE 5 - Conformité & Documentation - Loi 25');

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - RAPPORT D\'INCIDENT TI ===',
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          phase5,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour générer un rapport d\'incident complet et professionnel.',
          '- Chaque information doit être intégrée dans la section appropriée du rapport.',
          '- Ne générez PAS un rapport générique - chaque élément doit refléter les détails spécifiques de l\'incident documenté.',
          '- Créez des tableaux chronologiques, des matrices d\'impact, et des synthèses visuelles pour améliorer la lisibilité.',
          '- Respectez la structure ITIL 4 pour la gestion d\'incidents et intégrez les références aux cadres normatifs (ITIL 4, COBIT 2019, ISO 27001, NIST CSF 2.0).',
        ].join('\n');
      } else if (documentType.code === 'POLITIQUE_IA') {
        // Contexte spécifique pour Politique d'utilisation de l'IA
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bIa\b/g, 'IA')
                .replace(/\bAi\b/g, 'AI')
                .replace(/\bRmf\b/g, 'RMF')
                .replace(/\bLoi25\b/g, 'Loi 25');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Contexte & Gouvernance IA');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Cartographie des Risques IA (NIST AI RMF MAP)');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Mesure & Gestion des Risques IA (NIST AI RMF MEASURE & MANAGE)');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Éthique & Responsabilités');

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - POLITIQUE D\'UTILISATION DE L\'IA ===',
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section de la politique IA.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les règles, comparaisons par cas d\'usage, et matrices de décision.',
          '- Respectez la structure NIST AI RMF (GOVERN, MAP, MEASURE, MANAGE) et intégrez les références aux cadres normatifs (NIST AI RMF, ITIL 4, COBIT 2019, ISO 27001).',
        ].join('\n');
      } else if (documentType.code === 'INCIDENT_RESPONSE_POLICY' || documentType.code === 'INFORMATION_SECURITY_POLICY' || documentType.code === 'INCIDENT_CLASSIFICATION_POLICY' || documentType.code === 'INCIDENT_ESCALATION_POLICY') {
        // Contexte spécifique pour les politiques de gestion d'incidents
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bItil\b/g, 'ITIL')
                .replace(/\bNist\b/g, 'NIST')
                .replace(/\bCobit\b/g, 'COBIT');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Contexte & Objectifs');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Gouvernance & Responsabilités');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Enjeux & Risques / Principes & Contrôles / Niveaux / Critères');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Processus & Procédures / Mise en Œuvre / Matrice & Processus / Processus & Documentation');

        const docTypeName = documentType.code === 'INCIDENT_RESPONSE_POLICY' ? 'POLITIQUE DE GESTION DES INCIDENTS' :
                          documentType.code === 'INFORMATION_SECURITY_POLICY' ? 'POLITIQUE DE SÉCURITÉ DE L\'INFORMATION' :
                          documentType.code === 'INCIDENT_CLASSIFICATION_POLICY' ? 'POLITIQUE DE CLASSIFICATION DES INCIDENTS' :
                          'POLITIQUE D\'ESCALADE DES INCIDENTS';

        context = [
          `=== DONNÉES DE LA PRISE DE BESOIN - ${docTypeName} ===`,
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section de la politique.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les rôles, responsabilités, processus, et workflows.',
          '- Respectez la structure ITIL 4, COBIT 2019, ISO 27001, NIST CSF 2.0 et intégrez les références aux cadres normatifs.',
        ].join('\n');
      } else if (documentType.code === 'INCIDENT_RESPONSE_PLAN' || documentType.code === 'MAJOR_INCIDENT_PLAN' || documentType.code === 'INCIDENT_COMMUNICATION_PLAN' || documentType.code === 'ITSCM_PLAN' || documentType.code === 'PRIVACY_INCIDENT_PLAN') {
        // Contexte spécifique pour les plans tactiques de cybersécurité
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bItil\b/g, 'ITIL')
                .replace(/\bNist\b/g, 'NIST')
                .replace(/\bCobit\b/g, 'COBIT')
                .replace(/\bRto\b/g, 'RTO')
                .replace(/\bRpo\b/g, 'RPO')
                .replace(/\bCai\b/g, 'CAI')
                .replace(/\bDpo\b/g, 'DPO');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Contexte & Équipe / Définition / Audiences / Services Critiques');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Détection & Analyse / Équipe / Canaux / Sauvegarde');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Confinement & Éradication / Workflows / Sites Secours / Notification CAI');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Restauration & Post-Incident / Communication / Tests / Mesures Correctives');

        const docTypeName = documentType.code === 'INCIDENT_RESPONSE_PLAN' ? 'PLAN D\'INTERVENTION EN CAS D\'INCIDENT (IRP)' :
                          documentType.code === 'MAJOR_INCIDENT_PLAN' ? 'PLAN D\'INCIDENT MAJEUR' :
                          documentType.code === 'INCIDENT_COMMUNICATION_PLAN' ? 'PLAN DE COMMUNICATION D\'INCIDENT' :
                          documentType.code === 'ITSCM_PLAN' ? 'PLAN DE CONTINUITÉ TI (ITSCM)' :
                          'PLAN D\'INCIDENT VIE PRIVÉE / CONFIDENTIALITÉ';

        context = [
          `=== DONNÉES DE LA PRISE DE BESOIN - ${docTypeName} ===`,
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section du plan.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les workflows, rôles, procédures, et processus.',
          '- Respectez la structure NIST CSF 2.0, ITIL 4, ISO 27001, COBIT 2019, Loi 25 selon le type de plan et intégrez les références aux cadres normatifs.',
        ].join('\n');
      } else if (documentType.code === 'INCIDENT_MANAGEMENT_PROCEDURE' || documentType.code === 'SECURITY_INCIDENT_RESPONSE_PROCEDURE' || documentType.code === 'ESCALATION_PROCEDURE' || documentType.code === 'EVIDENCE_MANAGEMENT_PROCEDURE') {
        // Contexte spécifique pour les procédures opérationnelles
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bItil\b/g, 'ITIL')
                .replace(/\bNist\b/g, 'NIST')
                .replace(/\bCobit\b/g, 'COBIT')
                .replace(/\bRto\b/g, 'RTO')
                .replace(/\bRpo\b/g, 'RPO');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Contexte & Enregistrement / Détection & Analyse / Structure d\'Escalade / Contexte & Types de Preuves');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Classification & Priorisation / Confinement & Éradication / Critères & Délais / Collecte & Préservation');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Résolution & Workflows / Restauration & Post-Incident / Communication & Documentation / Analyse & Documentation');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Fermeture & Amélioration / Documentation & Amélioration / Amélioration Continue / Gestion & Amélioration');

        const docTypeName = documentType.code === 'INCIDENT_MANAGEMENT_PROCEDURE' ? 'PROCÉDURE DE GESTION DES INCIDENTS (ITIL)' :
                          documentType.code === 'SECURITY_INCIDENT_RESPONSE_PROCEDURE' ? 'PROCÉDURE DE RÉPONSE AUX INCIDENTS DE SÉCURITÉ (NIST)' :
                          documentType.code === 'ESCALATION_PROCEDURE' ? 'PROCÉDURE D\'ESCALADE' :
                          'PROCÉDURE DE GESTION DES PREUVES';

        context = [
          `=== DONNÉES DE LA PRISE DE BESOIN - ${docTypeName} ===`,
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section de la procédure.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les workflows, rôles, procédures, et processus.',
          '- Respectez la structure ITIL 4, NIST CSF 2.0, ISO 27001, COBIT 2019 selon le type de procédure et intégrez les références aux cadres normatifs.',
        ].join('\n');
      } else if (documentType.code === 'POST_INCIDENT_REVIEW' || documentType.code === 'AFTER_ACTION_REPORT' || documentType.code === 'LESSONS_LEARNED_REPORT') {
        // Contexte spécifique pour les documents post-incident (5 phases)
        const p1 = besoin.phase1_data || {};
        const p2 = besoin.phase2_data || {};
        const p3 = besoin.phase3_data || {};
        const p4 = besoin.phase4_data || {};
        const p5 = besoin.phase5_data || {};
        
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bItil\b/g, 'ITIL')
                .replace(/\bNist\b/g, 'NIST')
                .replace(/\bCobit\b/g, 'COBIT')
                .replace(/\bMttd\b/g, 'MTTD')
                .replace(/\bMttr\b/g, 'MTTR');
              const formattedValue = Array.isArray(value) 
                ? value.join(', ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };
        const phase1 = formatPhaseData(p1, 'PHASE 1 - Contexte & Identification');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Chronologie & Détection / Chronologie Détaillée / Ce qui a Bien Fonctionné');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - Analyse & Impact / Actions & Résultats / Ce qui Doit Être Amélioré');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Réponse & Résolution / Analyse & Impact / Leçons Apprises');
        const phase5 = formatPhaseData(p5, 'PHASE 5 - Leçons Apprises & Amélioration / Recommandations & Suivi / Actions Correctives & Suivi');

        const docTypeName = documentType.code === 'POST_INCIDENT_REVIEW' ? 'POST-INCIDENT REVIEW (PIR)' :
                          documentType.code === 'AFTER_ACTION_REPORT' ? 'AFTER-ACTION REPORT (AAR)' :
                          'RAPPORT DE LEÇONS APPRISES';

        context = [
          `=== DONNÉES DE LA PRISE DE BESOIN - ${docTypeName} ===`,
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          phase5,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS IMPORTANTES:',
          '- Utilisez TOUTES ces données pour personnaliser chaque section du rapport.',
          '- Chaque réponse doit être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les choix spécifiques du client.',
          '- Créez des tableaux synthèse pour visualiser les chronologies, analyses, métriques, et synthèses.',
          '- Respectez la structure ITIL 4, NIST CSF 2.0, ISO 27001, COBIT 2019 selon le type de rapport et intégrez les références aux cadres normatifs.',
        ].join('\n');
      } else if (documentType.code === 'PLAN_DIRECTEUR_TECHNIQUE') {
        // Contexte spécifique pour Plan Directeur TI - Analyse Technique & Budget
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              // Formater la clé pour être plus lisible
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bRto\b/g, 'RTO')
                .replace(/\bRpo\b/g, 'RPO')
                .replace(/\b2fa\b/g, '2FA')
                .replace(/\bEdr\b/g, 'EDR')
                .replace(/\bTi\b/g, 'TI')
                .replace(/\bNas\b/g, 'NAS')
                .replace(/\bSan\b/g, 'SAN')
                .replace(/\bUps\b/g, 'UPS')
                .replace(/\bCal\b/g, 'CAL');
              const formattedValue = Array.isArray(value) 
                ? value.join('\n  ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              // Pour les textarea longs, préserver les retours à la ligne
              const finalValue = formattedValue.includes('\n') 
                ? '\n  ' + formattedValue.split('\n').join('\n  ')
                : formattedValue;
              return `- ${label}: ${finalValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Qualification Client');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Infrastructure & Équipements');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - RTO & RPO');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Sauvegardes & Hébergement');
        const phase5 = formatPhaseData(p5, 'PHASE 5 - Sécurité & Conformité');
        const phase6 = formatPhaseData(p6, 'PHASE 6 - Logiciels & Licences');
        const phase7 = formatPhaseData(p7, 'PHASE 7 - Budget & Investissements');
        const phase8 = formatPhaseData(p8, 'PHASE 8 - Programmation, Télécom & Autres');
        const phase9 = formatPhaseData(p9, 'PHASE 9 - Formation & Sensibilisation');

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI - ANALYSE TECHNIQUE & BUDGET ===',
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          phase5,
          '',
          phase6,
          '',
          phase7,
          '',
          phase8,
          '',
          phase9,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- OBLIGATOIRE d\'utiliser TOUTES les données fournies ci-dessus pour personnaliser chaque section du document.',
          '- Chaque réponse de la prise de besoin DOIT être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les données spécifiques du client.',
          '- Pour les listes Excel (serveurs, stockage, UPS, réseau, postes, logiciels): Utilisez les données EXACTES fournies pour créer des tableaux Markdown détaillés.',
          '- Pour le plan Visio: Si "plan_visio" est présent dans phase2_data, VOUS DEVEZ ABSOLUMENT : 1) Décrire l\'architecture réseau en utilisant ces informations, 2) Créer le placeholder EXACT {{IMAGE:INFRASTRUCTURE_NETWORK}} juste après la description, 3) Ne pas oublier ce placeholder - il est essentiel.',
          '- Pour RTO/RPO: Utilisez les valeurs EXACTES du tableau fourni. Créez le graphique spider chart avec ces valeurs.',
          '- Pour le budget: Utilisez les montants EXACTS fournis. Si des montants sont fournis, créez les tableaux budgétaires détaillés avec ces valeurs.',
          '- Pour les stratégies de sauvegardes: Utilisez les informations EXACTES fournies (fréquences, outils, localisations).',
          '- Pour la conformité Loi 25: Utilisez les réponses EXACTES pour créer la checklist et le graphique.',
          '- Créez des tableaux Markdown professionnels pour toutes les données structurées (serveurs, stockage, logiciels, budget, RTO/RPO, etc.).',
          '- Intégrez les placeholders de graphiques SVG pour visualiser les données (RTO/RPO, sauvegardes, budget, conformité).',
          '- Le document doit être 100% en français, sans mots anglais, sans marqueurs markdown isolés (---, }), et sans placeholders non remplacés.',
        ].join('\n');
      } else if (documentType.code === 'PLAN_DIRECTEUR_TECHNIQUE') {
        // Contexte spécifique pour Plan Directeur TI - Analyse Technique & Budget
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              // Formater la clé pour être plus lisible
              const label = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .replace(/\bRto\b/g, 'RTO')
                .replace(/\bRpo\b/g, 'RPO')
                .replace(/\b2fa\b/g, '2FA')
                .replace(/\bEdr\b/g, 'EDR')
                .replace(/\bTi\b/g, 'TI')
                .replace(/\bNas\b/g, 'NAS')
                .replace(/\bSan\b/g, 'SAN')
                .replace(/\bUps\b/g, 'UPS')
                .replace(/\bCal\b/g, 'CAL');
              const formattedValue = Array.isArray(value) 
                ? value.join('\n  ') 
                : typeof value === 'object' 
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              // Pour les textarea longs, préserver les retours à la ligne
              const finalValue = formattedValue.includes('\n') 
                ? '\n  ' + formattedValue.split('\n').join('\n  ')
                : formattedValue;
              return `- ${label}: ${finalValue}`;
            });
          return entries.length > 0 
            ? `${phaseName}:\n${entries.join('\n')}`
            : `${phaseName}:\n- Aucune donnée disponible`;
        };

        const phase1 = formatPhaseData(p1, 'PHASE 1 - Qualification Client');
        const phase2 = formatPhaseData(p2, 'PHASE 2 - Infrastructure & Équipements');
        const phase3 = formatPhaseData(p3, 'PHASE 3 - RTO & RPO');
        const phase4 = formatPhaseData(p4, 'PHASE 4 - Sauvegardes & Hébergement');
        const phase5 = formatPhaseData(p5, 'PHASE 5 - Sécurité & Conformité');
        const phase6 = formatPhaseData(p6, 'PHASE 6 - Logiciels & Licences');
        const phase7 = formatPhaseData(p7, 'PHASE 7 - Budget & Investissements');
        const phase8 = formatPhaseData(p8, 'PHASE 8 - Programmation, Télécom & Autres');
        const phase9 = formatPhaseData(p9, 'PHASE 9 - Formation & Sensibilisation');

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI - ANALYSE TECHNIQUE & BUDGET ===',
          '',
          phase1,
          '',
          phase2,
          '',
          phase3,
          '',
          phase4,
          '',
          phase5,
          '',
          phase6,
          '',
          phase7,
          '',
          phase8,
          '',
          phase9,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- OBLIGATOIRE d\'utiliser TOUTES les données fournies ci-dessus pour personnaliser chaque section du document.',
          '- Chaque réponse de la prise de besoin DOIT être intégrée dans la section appropriée du document.',
          '- Ne générez PAS un document générique - chaque élément doit refléter les données spécifiques du client.',
          '- Pour les listes Excel (serveurs, stockage, UPS, réseau, postes, logiciels): Utilisez les données EXACTES fournies pour créer des tableaux Markdown détaillés.',
          '- Pour le plan Visio: Si "plan_visio" est présent dans phase2_data, VOUS DEVEZ ABSOLUMENT : 1) Décrire l\'architecture réseau en utilisant ces informations, 2) Créer le placeholder EXACT {{IMAGE:INFRASTRUCTURE_NETWORK}} juste après la description, 3) Ne pas oublier ce placeholder - il est essentiel.',
          '- Pour RTO/RPO: Utilisez les valeurs EXACTES du tableau fourni. Créez le graphique spider chart avec ces valeurs.',
          '- Pour le budget: Utilisez les montants EXACTS fournis. Si des montants sont fournis, créez les tableaux budgétaires détaillés avec ces valeurs.',
          '- Pour les stratégies de sauvegardes: Utilisez les informations EXACTES fournies (fréquences, outils, localisations).',
          '- Pour la conformité Loi 25: Utilisez les réponses EXACTES pour créer la checklist et le graphique.',
          '- Créez des tableaux Markdown professionnels pour toutes les données structurées (serveurs, stockage, logiciels, budget, RTO/RPO, etc.).',
          '- Intégrez les placeholders de graphiques SVG pour visualiser les données (RTO/RPO, sauvegardes, budget, conformité).',
          '- Le document doit être 100% en français, sans mots anglais, sans marqueurs markdown isolés (---, }), et sans placeholders non remplacés.',
        ].join('\n');
      } else if (documentType.code === 'PLAN_DIRECTEUR' && (besoin as any).template_id === 'pdti-pme-v2') {
        // Prise de besoin Plan Directeur TI – PME V2 : 7 piliers BZ + section « Déjà en place »
        const formatPhaseData = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) {
            return `${phaseName}:\n- Aucune donnée disponible`;
          }
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
              const formattedValue = Array.isArray(value)
                ? value.join(', ')
                : typeof value === 'object'
                  ? JSON.stringify(value, null, 2)
                  : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 ? `${phaseName}:\n${entries.join('\n')}` : `${phaseName}:\n- Aucune donnée disponible`;
        };
        const ph1 = formatPhaseData(p1, 'Phase 1 - Qualification Client');
        const ph2 = formatPhaseData(p2, 'Phase 2 - Stratégie & Gouvernance TI');
        const ph3 = formatPhaseData(p3, 'Phase 3 - Gestion des actifs & parties prenantes');
        const ph4 = formatPhaseData(p4, 'Phase 4 - Gestion de projet');
        const ph5 = formatPhaseData(p5, 'Phase 5 - Gestion des Services TI');
        const ph6 = formatPhaseData(p6, 'Phase 6 - Cybersécurité');
        const ph7 = formatPhaseData(p7, 'Phase 7 - Amélioration continue / maturité TI');
        const ph8 = formatPhaseData(p8, 'Phase 8 - Innovation & IA');
        const ph9 = formatPhaseData(p9, 'Phase 9 - Déjà en place / Déjà fait');
        const metierV2 = pm && Object.keys(pm).length > 0
          ? `Contexte métier (secteur d'activité, processus, besoins spécifiques):\n${JSON.stringify(pm, null, 2)}`
          : '';
        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI – PME V2 (7 PILIERS BZ) ===',
          '',
          ph1,
          '',
          ph2,
          '',
          ph3,
          '',
          ph4,
          '',
          ph5,
          '',
          ph6,
          '',
          ph7,
          '',
          ph8,
          '',
          ph9,
          ...(metierV2 ? ['', metierV2] : []),
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- Le document doit être intégralement basé sur les données ci-dessus. Aucune donnée fictive.',
          '- Utiliser les 7 PILIERS BZ pour structurer les sections, tableaux et graphiques (spider/radar) : 1) Stratégie & Gouvernance TI, 2) Gestion des actifs & parties prenantes, 3) Gestion de projet, 4) Gestion des Services TI, 5) Cybersécurité, 6) Amélioration continue / maturité TI, 7) Innovation & IA.',
          '- NE PAS recommander ni proposer des actions pour les éléments listés dans la section « Phase 9 - Déjà en place / Déjà fait ». Ces éléments sont déjà en place ou déjà réalisés.',
          '- Chaque section du Plan Directeur doit refléter les réponses par pilier et la section déjà en place.',
        ].join('\n\n');
        const hasMinimalDataV2 =
          context.length >= 200 && (p1.company_name || p2.main_challenge || (p2.business_goals && (p2.business_goals as string[]).length > 0));
        if (!hasMinimalDataV2) {
          window.clearInterval(intervalId);
          setError(
            'La prise de besoin Plan Directeur TI – PME V2 sélectionnée ne contient pas assez de données. Veuillez compléter au minimum : nom de l\'organisation ou défi TI principal, et objectifs.'
          );
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else if (documentType.code === 'PLAN_DIRECTEUR' && (besoin as any).template_id === 'prise-de-besoin-client-2026') {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        context = buildClient2026StrategicContext(selectedClient?.nom || 'Client');

        if (!client2026HasUsableData()) {
          window.clearInterval(intervalId);
          setError(
            'La prise de besoin - Maturité et besoins client sélectionnée ne contient pas assez de données pour générer le Plan Directeur. Veuillez compléter au moins quelques réponses, priorités, objectifs SMART, solutions, risques ou mitigations.'
          );
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else if (documentType.code === 'PLAN_DIRECTEUR') {
        // Bloc dédié : Plan Directeur TI - Vision Stratégique (3-5 ans)
        // Contexte UNIQUEMENT à partir de la prise de besoin "Prise de besoin Plan Directeur TI – PME".
        // Génération par OpenAI uniquement (serveur ai-documents.js), pas de données mock.
        
        // Pour le Plan budgétaire TI annuel (N+1), diviser le budget 5 ans par 5
        // IMPORTANT: Utiliser le budget (budget_range ou budget_3_5y_global) et NON le chiffre d'affaires (revenue)
        let budgetAnnuel = p2.budget_range || 'Non précisé';
        let budget5Ans = p2.budget_3_5y_global || 'Non précisé';
        
        if (documentTypeCode === 'PLAN_BUDGETAIRE_TI_N1') {
          // Priorité 1: Utiliser budget_3_5y_global (budget 5 ans) si disponible
          // Priorité 2: Utiliser budget_range (Budget estimé pour ce projet) si budget_3_5y_global n'est pas disponible
          let budgetSource = p2.budget_3_5y_global || p2.budget_range;
          
          if (budgetSource) {
            // Extraire le montant numérique du budget et le diviser par 5
            const budgetStr = String(budgetSource);
            // Chercher un nombre dans le texte (peut être "100K$", "250K$ - 500K$", "250000", "1M$", etc.)
            // Pour les fourchettes, prendre la valeur moyenne
            const rangeMatch = budgetStr.match(/(\d+(?:\.\d+)?)\s*([KM]?)\$?\s*-\s*(\d+(?:\.\d+)?)\s*([KM]?)\$?/i);
            const singleMatch = budgetStr.match(/(\d+(?:\.\d+)?)\s*([KM]?)\$?/i);
            
            let montant = 0;
            
            if (rangeMatch) {
              // Fourchette: prendre la moyenne
              let min = parseFloat(rangeMatch[1]);
              let max = parseFloat(rangeMatch[3]);
              const uniteMin = rangeMatch[2].toUpperCase();
              const uniteMax = rangeMatch[4].toUpperCase();
              
              if (uniteMin === 'K') min = min * 1000;
              else if (uniteMin === 'M') min = min * 1000000;
              
              if (uniteMax === 'K') max = max * 1000;
              else if (uniteMax === 'M') max = max * 1000000;
              
              montant = (min + max) / 2;
            } else if (singleMatch) {
              // Valeur unique
              montant = parseFloat(singleMatch[1]);
              const unite = singleMatch[2].toUpperCase();
              
              if (unite === 'K') {
                montant = montant * 1000;
              } else if (unite === 'M') {
                montant = montant * 1000000;
              }
            }
            
            if (montant > 0) {
              // Diviser par 5 pour obtenir le budget annuel (le budget dans la prise de besoin est pour 5 ans)
              const budgetAnnuelMontant = montant / 5;
              
              // Formater le résultat de manière lisible
              if (budgetAnnuelMontant >= 1000000) {
                budgetAnnuel = `${(budgetAnnuelMontant / 1000000).toFixed(1)}M$`;
              } else if (budgetAnnuelMontant >= 1000) {
                budgetAnnuel = `${(budgetAnnuelMontant / 1000).toFixed(0)}K$`;
              } else {
                budgetAnnuel = `${Math.round(budgetAnnuelMontant)}$`;
              }
              
              // Mettre à jour aussi budget_3_5y_global pour indiquer que c'est le budget annuel
              const sourceLabel = p2.budget_3_5y_global ? 'budget 5 ans' : 'budget estimé pour ce projet';
              budget5Ans = `${budgetAnnuel} (budget annuel N+1, calculé à partir du ${sourceLabel}: ${budgetSource})`;
            }
          }
        }
        
        const contexteClient = `Profil du client:
- Nom de l'organisation: ${p1.company_name || 'Non précisé'}
- Type d'organisation: ${p1.company_type || 'Non précisé'}
- Secteur / industrie: ${p1.industry || 'Non précisé'}
- Taille (employés): ${p1.employees || 'Non précisé'}
- Chiffre d'affaires approximatif: ${p1.revenue || 'Non précisé'}
- Maturité TI actuelle: ${p1.it_maturity || 'Non précisé'}
- Configuration de l'équipe TI: ${p1.it_team || 'Non précisé'}

Objectifs et contexte d'affaires:
- Défi TI principal: ${p2.main_challenge || 'Non précisé'}
- Objectifs d'affaires clés: ${(p2.business_goals || []).join(', ') || 'Non précisé'}
- Objectifs TI prioritaires: ${(p2.it_goals || []).join(', ') || 'Non précisé'}
- Horizon temporel: ${p2.timeline || 'Non précisé'}
- Budget estimé initial (année 1): ${budgetAnnuel}
- Enveloppe budgétaire 3-5 ans: ${budget5Ans}
- Commentaires sur la répartition par pilier: ${p2.budget_pillars_comment || 'Non précisé'}
- Métriques de succès: ${p2.success_metrics || 'Non précisé'}`;

        const postureTech = `Posture technique actuelle (AS-IS):
- Systèmes critiques actuels: ${(p3.current_systems || []).join(', ') || 'Non précisé'}
- Infrastructure actuelle: ${(p3.infrastructure || []).join(', ') || 'Non précisé'}
- Points de douleur principaux: ${(p3.pain_points || []).join(', ') || 'Non précisé'}
- Priorités TI perçues: ${(p3.priorities || []).join(', ') || 'Non précisé'}
- Préoccupations de sécurité: ${(p3.security_concerns || []).join(', ') || 'Non précisé'}
- Besoins d'intégration: ${p3.integration_needs || 'Non précisé'}
- Nombre d'utilisateurs impactés: ${p3.users_impacted || 'Non précisé'}`;

        const gouvernance = `Gouvernance & support:
- Décideurs clés: ${p4.decision_makers || 'Non précisé'}
- Principales parties prenantes: ${(p4.stakeholders || []).join(', ') || 'Non précisé'}
- Préparation au changement: ${p4.change_readiness || 'Non précisé'}
- Besoins de formation: ${p4.training_needs || 'Non précisé'}
- Modèle de support souhaité: ${p4.support_model || 'Non précisé'}
- Informations additionnelles: ${p4.additional_info || 'Non précisé'}`;

        const metier = pm
          ? `Contexte métier spécifique (page métier):
${JSON.stringify(pm, null, 2)}`
          : 'Contexte métier spécifique: non renseigné';

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI – PME ===',
          '',
          contexteClient,
          '',
          postureTech,
          '',
          gouvernance,
          '',
          metier,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- Le document doit être intégralement basé sur les données ci-dessus. Aucune donnée fictive ou "mock".',
          '- Chaque section du Plan Directeur doit refléter les réponses de la prise de besoin (contexte, objectifs, posture TI, gouvernance).',
          '- Analyser et générer le contenu avec OpenAI en vous appuyant uniquement sur ce contexte client.',
        ].join('\n\n');

        const hasMinimalData =
          context.length >= 300 &&
          (p1.company_name || p2.main_challenge || (p2.business_goals && (p2.business_goals as string[]).length > 0));
        if (!hasMinimalData) {
          window.clearInterval(intervalId);
          setError(
            'La prise de besoin sélectionnée ne contient pas assez de données pour générer le Plan Directeur. Veuillez compléter la prise de besoin Plan Directeur TI – PME (au minimum : nom de l\'organisation ou défi TI principal, et objectifs).'
          );
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else if (documentType.code === 'ROADMAP_24M' && (besoin as any).template_id === 'pdti-pme-v2') {
        // Feuille de route 12-24 mois avec prise de besoin V2 (7 piliers BZ + déjà en place)
        const formatPhaseDataR = (phaseData: any, phaseName: string) => {
          if (!phaseData || Object.keys(phaseData).length === 0) return `${phaseName}:\n- Aucune donnée disponible`;
          const entries = Object.entries(phaseData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => {
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
              const formattedValue = Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
              return `- ${label}: ${formattedValue}`;
            });
          return entries.length > 0 ? `${phaseName}:\n${entries.join('\n')}` : `${phaseName}:\n- Aucune donnée disponible`;
        };
        const rph1 = formatPhaseDataR(p1, 'Phase 1 - Qualification Client');
        const rph2 = formatPhaseDataR(p2, 'Phase 2 - Stratégie & Gouvernance TI');
        const rph3 = formatPhaseDataR(p3, 'Phase 3 - Gestion des actifs & parties prenantes');
        const rph4 = formatPhaseDataR(p4, 'Phase 4 - Gestion de projet');
        const rph5 = formatPhaseDataR(p5, 'Phase 5 - Gestion des Services TI');
        const rph6 = formatPhaseDataR(p6, 'Phase 6 - Cybersécurité');
        const rph7 = formatPhaseDataR(p7, 'Phase 7 - Amélioration continue / maturité TI');
        const rph8 = formatPhaseDataR(p8, 'Phase 8 - Innovation & IA');
        const rph9 = formatPhaseDataR(p9, 'Phase 9 - Déjà en place / Déjà fait');
        const metierRoadmapV2 = pm && Object.keys(pm).length > 0
          ? `Contexte métier:\n${JSON.stringify(pm, null, 2)}`
          : '';
        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - FEUILLE DE ROUTE 12-24 MOIS (7 PILIERS BZ – PME V2) ===',
          '', rph1, '', rph2, '', rph3, '', rph4, '', rph5, '', rph6, '', rph7, '', rph8, '', rph9,
          ...(metierRoadmapV2 ? ['', metierRoadmapV2] : []),
          '',
          '=== FIN DES DONNÉES ===',
          '',
          'INSTRUCTIONS: Utiliser les 7 PILIERS BZ pour la structure et les visualisations. Ne pas recommander ce qui est listé en Phase 9 (Déjà en place).',
        ].flat().join('\n\n');
        const hasMinimalRoadmapV2 = context.length >= 200 && (p1.company_name || p2.main_challenge);
        if (!hasMinimalRoadmapV2) {
          window.clearInterval(intervalId);
          setError('La prise de besoin V2 sélectionnée ne contient pas assez de données pour la Feuille de route.');
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else if (documentType.code === 'ROADMAP_24M') {
        // Feuille de route TI (12-24 mois) : même source que le Plan Directeur – Prise de besoin Plan Directeur TI – PME.
        const contexteClient = `Profil du client:
- Nom de l'organisation: ${p1.company_name || 'Non précisé'}
- Type d'organisation: ${p1.company_type || 'Non précisé'}
- Secteur / industrie: ${p1.industry || 'Non précisé'}
- Taille (employés): ${p1.employees || 'Non précisé'}
- Chiffre d'affaires approximatif: ${p1.revenue || 'Non précisé'}
- Maturité TI actuelle: ${p1.it_maturity || 'Non précisé'}
- Configuration de l'équipe TI: ${p1.it_team || 'Non précisé'}

Objectifs et contexte d'affaires:
- Défi TI principal: ${p2.main_challenge || 'Non précisé'}
- Objectifs d'affaires clés: ${(p2.business_goals || []).join(', ') || 'Non précisé'}
- Objectifs TI prioritaires: ${(p2.it_goals || []).join(', ') || 'Non précisé'}
- Horizon temporel: ${p2.timeline || 'Non précisé'}
- Budget estimé initial (année 1): ${p2.budget_range || 'Non précisé'}
- Enveloppe budgétaire 3-5 ans: ${p2.budget_3_5y_global || 'Non précisé'}
- Commentaires sur la répartition par pilier: ${p2.budget_pillars_comment || 'Non précisé'}
- Métriques de succès: ${p2.success_metrics || 'Non précisé'}`;

        const postureTech = `Posture technique actuelle (AS-IS):
- Systèmes critiques actuels: ${(p3.current_systems || []).join(', ') || 'Non précisé'}
- Infrastructure actuelle: ${(p3.infrastructure || []).join(', ') || 'Non précisé'}
- Points de douleur principaux: ${(p3.pain_points || []).join(', ') || 'Non précisé'}
- Priorités TI perçues: ${(p3.priorities || []).join(', ') || 'Non précisé'}
- Préoccupations de sécurité: ${(p3.security_concerns || []).join(', ') || 'Non précisé'}
- Besoins d'intégration: ${p3.integration_needs || 'Non précisé'}
- Nombre d'utilisateurs impactés: ${p3.users_impacted || 'Non précisé'}`;

        const gouvernance = `Gouvernance & support:
- Décideurs clés: ${p4.decision_makers || 'Non précisé'}
- Principales parties prenantes: ${(p4.stakeholders || []).join(', ') || 'Non précisé'}
- Préparation au changement: ${p4.change_readiness || 'Non précisé'}
- Besoins de formation: ${p4.training_needs || 'Non précisé'}
- Modèle de support souhaité: ${p4.support_model || 'Non précisé'}
- Informations additionnelles: ${p4.additional_info || 'Non précisé'}`;

        const metier = pm
          ? `Contexte métier spécifique (page métier):
${JSON.stringify(pm, null, 2)}`
          : 'Contexte métier spécifique: non renseigné';

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI – PME (Feuille de route 12-24 mois) ===',
          '',
          contexteClient,
          '',
          postureTech,
          '',
          gouvernance,
          '',
          metier,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- La Feuille de route TI 12-24 mois doit être intégralement basée sur les données ci-dessus. Aucune donnée fictive ou "mock".',
          '- Elle doit s\'aligner sur les mêmes piliers et priorités que le Plan Directeur (contexte, objectifs, posture TI, gouvernance).',
          '- Analyser et générer le contenu avec OpenAI en vous appuyant uniquement sur ce contexte client.',
        ].join('\n\n');

        const hasMinimalData =
          context.length >= 300 &&
          (p1.company_name || p2.main_challenge || (p2.business_goals && (p2.business_goals as string[]).length > 0));
        if (!hasMinimalData) {
          window.clearInterval(intervalId);
          setError(
            'La prise de besoin sélectionnée ne contient pas assez de données pour générer la Feuille de route. Veuillez compléter la prise de besoin Plan Directeur TI – PME (au minimum : nom de l\'organisation ou défi TI principal, et objectifs).'
          );
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else if ((besoin as any).template_id === 'pdti-pme-v3') {
        // Prise de besoin Plan Directeur TI – PME V3 : langage client + mapping BZ en arrière-plan.
        const v3ph1 = formatPhaseData(p1, 'Phase 1 - Profil et contexte d’affaires');
        const v3ph2 = formatPhaseData(p2, 'Phase 2 - Objectifs, ambitions et résultats attendus');
        const v3ph3 = formatPhaseData(p3, 'Phase 3 - Irritants, risques et contraintes');
        const v3ph4 = formatPhaseData(p4, 'Phase 4 - Processus critiques, systèmes et données');
        const v3ph5 = formatPhaseData(p5, 'Phase 5 - Opérations quotidiennes et expérience utilisateur');
        const v3ph6 = formatPhaseData(p6, 'Phase 6 - Sécurité, conformité et continuité');
        const v3ph7 = formatPhaseData(p7, 'Phase 7 - Budget, appétit et choix d’accompagnement');
        const v3ph8 = formatPhaseData(p8, 'Phase 8 - Déjà en place, à conserver ou à exclure');
        const metierV3 = pm && Object.keys(pm).length > 0
          ? `Page métier spécifique:\n${JSON.stringify(pm, null, 2)}`
          : 'Page métier spécifique:\n- Non renseignée';

        context = [
          '=== DONNÉES DE LA PRISE DE BESOIN - PLAN DIRECTEUR TI – PME V3 ===',
          '',
          'POSITIONNEMENT V3:',
          '- La collecte est rédigée en langage client et besoins d’affaires.',
          '- Le cadre BZ 6 + 2 sert de grille d’analyse interne et de présentation finale, pas de questionnaire client.',
          '- Pour une Offre de service BZ, utiliser le budget mensuel, l’appétit client, les irritants, les objectifs, les contraintes, le déjà en place et la page métier pour proposer 3 choix comparables.',
          '',
          v3ph1,
          '',
          v3ph2,
          '',
          v3ph3,
          '',
          v3ph4,
          '',
          v3ph5,
          '',
          v3ph6,
          '',
          v3ph7,
          '',
          v3ph8,
          '',
          metierV3,
          '',
          '=== FIN DES DONNÉES DE LA PRISE DE BESOIN V3 ===',
          '',
          'INSTRUCTIONS CRITIQUES:',
          '- Le document doit répondre aux besoins d’affaires exprimés par le client, sans forcer le client à parler en piliers.',
          '- Traduire ensuite les besoins vers le cadre BZ 6 + 2, les tactiques, les livrables et les options de service.',
          '- Ne pas recommander ce qui est indiqué comme déjà en place, à conserver ou à exclure.',
          '- Utiliser la page métier pour personnaliser les enjeux, risques, livrables et exemples.',
          '- Pour OFFRE_SERVICE_BZ, présenter clairement 3 choix: Essentielle, Recommandée et Complète.',
        ].join('\n\n');

        const hasMinimalV3Data =
          context.length >= 300 &&
          (p1.company_name || p2.main_challenge || (p2.business_goals && (p2.business_goals as string[]).length > 0));
        if (!hasMinimalV3Data) {
          window.clearInterval(intervalId);
          setError(
            'La prise de besoin V3 sélectionnée ne contient pas assez de données. Veuillez compléter au minimum le profil client, l’enjeu principal et les objectifs d’affaires.'
          );
          setTimeout(() => setIsGenerating(false), 100);
          return;
        }
      } else {
        // Contexte pour les autres types de documents (pdti-standard-pme)
        
        // Pour le Plan budgétaire TI annuel (N+1), diviser le budget 5 ans par 5
        // IMPORTANT: Utiliser le budget (budget_range ou budget_3_5y_global) et NON le chiffre d'affaires (revenue)
        let budgetAnnuel = p2.budget_range || 'Non précisé';
        let budget5Ans = p2.budget_3_5y_global || 'Non précisé';
        
        if (documentTypeCode === 'PLAN_BUDGETAIRE_TI_N1') {
          // Priorité 1: Utiliser budget_3_5y_global (budget 5 ans) si disponible
          // Priorité 2: Utiliser budget_range (Budget estimé pour ce projet) si budget_3_5y_global n'est pas disponible
          let budgetSource = p2.budget_3_5y_global || p2.budget_range;
          
          if (budgetSource) {
            // Extraire le montant numérique du budget et le diviser par 5
            const budgetStr = String(budgetSource);
            // Chercher un nombre dans le texte (peut être "100K$", "250K$ - 500K$", "250000", "1M$", etc.)
            // Pour les fourchettes, prendre la valeur moyenne
            const rangeMatch = budgetStr.match(/(\d+(?:\.\d+)?)\s*([KM]?)\$?\s*-\s*(\d+(?:\.\d+)?)\s*([KM]?)\$?/i);
            const singleMatch = budgetStr.match(/(\d+(?:\.\d+)?)\s*([KM]?)\$?/i);
            
            let montant = 0;
            
            if (rangeMatch) {
              // Fourchette: prendre la moyenne
              let min = parseFloat(rangeMatch[1]);
              let max = parseFloat(rangeMatch[3]);
              const uniteMin = rangeMatch[2].toUpperCase();
              const uniteMax = rangeMatch[4].toUpperCase();
              
              if (uniteMin === 'K') min = min * 1000;
              else if (uniteMin === 'M') min = min * 1000000;
              
              if (uniteMax === 'K') max = max * 1000;
              else if (uniteMax === 'M') max = max * 1000000;
              
              montant = (min + max) / 2;
            } else if (singleMatch) {
              // Valeur unique
              montant = parseFloat(singleMatch[1]);
              const unite = singleMatch[2].toUpperCase();
              
              if (unite === 'K') {
                montant = montant * 1000;
              } else if (unite === 'M') {
                montant = montant * 1000000;
              }
            }
            
            if (montant > 0) {
              // Diviser par 5 pour obtenir le budget annuel (le budget dans la prise de besoin est pour 5 ans)
              const budgetAnnuelMontant = montant / 5;
              
              // Formater le résultat de manière lisible
              if (budgetAnnuelMontant >= 1000000) {
                budgetAnnuel = `${(budgetAnnuelMontant / 1000000).toFixed(1)}M$`;
              } else if (budgetAnnuelMontant >= 1000) {
                budgetAnnuel = `${(budgetAnnuelMontant / 1000).toFixed(0)}K$`;
              } else {
                budgetAnnuel = `${Math.round(budgetAnnuelMontant)}$`;
              }
              
              // Mettre à jour aussi budget_3_5y_global pour indiquer que c'est le budget annuel
              const sourceLabel = p2.budget_3_5y_global ? 'budget 5 ans' : 'budget estimé pour ce projet';
              budget5Ans = `${budgetAnnuel} (budget annuel N+1, calculé à partir du ${sourceLabel}: ${budgetSource})`;
            }
          }
        }
        
        const contexteClient = `Profil du client:
- Nom de l'organisation: ${p1.company_name || 'Non précisé'}
- Type d'organisation: ${p1.company_type || 'Non précisé'}
- Secteur / industrie: ${p1.industry || 'Non précisé'}
- Taille (employés): ${p1.employees || 'Non précisé'}
- Chiffre d'affaires approximatif: ${p1.revenue || 'Non précisé'}
- Maturité TI actuelle: ${p1.it_maturity || 'Non précisé'}
- Configuration de l'équipe TI: ${p1.it_team || 'Non précisé'}

Objectifs et contexte d'affaires:
- Défi TI principal: ${p2.main_challenge || 'Non précisé'}
- Objectifs d'affaires clés: ${(p2.business_goals || []).join(', ') || 'Non précisé'}
- Objectifs TI prioritaires: ${(p2.it_goals || []).join(', ') || 'Non précisé'}
- Horizon temporel: ${p2.timeline || 'Non précisé'}
- Budget estimé initial (année 1): ${budgetAnnuel}
- Enveloppe budgétaire 3-5 ans: ${budget5Ans}
- Commentaires sur la répartition par pilier: ${p2.budget_pillars_comment || 'Non précisé'}
- Métriques de succès: ${p2.success_metrics || 'Non précisé'}`;

        const postureTech = `Posture technique actuelle (AS-IS):
- Systèmes critiques actuels: ${(p3.current_systems || []).join(', ') || 'Non précisé'}
- Infrastructure actuelle: ${(p3.infrastructure || []).join(', ') || 'Non précisé'}
- Points de douleur principaux: ${(p3.pain_points || []).join(', ') || 'Non précisé'}
- Priorités TI perçues: ${(p3.priorities || []).join(', ') || 'Non précisé'}
- Préoccupations de sécurité: ${(p3.security_concerns || []).join(', ') || 'Non précisé'}
- Besoins d'intégration: ${p3.integration_needs || 'Non précisé'}
- Nombre d'utilisateurs impactés: ${p3.users_impacted || 'Non précisé'}`;

        const gouvernance = `Gouvernance & support:
- Décideurs clés: ${p4.decision_makers || 'Non précisé'}
- Principales parties prenantes: ${(p4.stakeholders || []).join(', ') || 'Non précisé'}
- Préparation au changement: ${p4.change_readiness || 'Non précisé'}
- Besoins de formation: ${p4.training_needs || 'Non précisé'}
- Modèle de support souhaité: ${p4.support_model || 'Non précisé'}
- Informations additionnelles: ${p4.additional_info || 'Non précisé'}`;

        const metier = pm
          ? `Contexte métier spécifique (page métier):
${JSON.stringify(pm, null, 2)}`
          : 'Contexte métier spécifique: non renseigné';

        context = [contexteClient, postureTech, gouvernance, metier].join('\n\n');
      }

      const qualityDef = QUALITY_LEVELS.find((q) => q.level === qualityLevel) || QUALITY_LEVELS[2];

      // Cas spécial : document tabulaire (pas de génération IA)
      if (documentType.code === 'PRISE_BESOIN_CLIENT_2026') {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientName = selectedClient?.nom || 'Client';
        const mergedClient2026Responses = mergeClient2026PhaseResponses(besoin as any);
        const client2026AsWasBaseline = await getOrCreateClient2026AsWasBaseline(
          selectedBesoinId!,
        );
        const client2026Responses = isLegacyClient2026Besoin
          ? mergedClient2026Responses
          : client2026SourceSchema === 2
            ? applyClient2026V3AsWasBaseline(mergedClient2026Responses, client2026AsWasBaseline)
            : applyClient2026AsWasBaseline(mergedClient2026Responses, client2026AsWasBaseline);

        const rows = isLegacyClient2026Besoin
          ? buildLegacyClient2026DocumentRows(client2026Responses)
          : client2026SourceSchema === 2
            ? buildClient2026V3DocumentRows(client2026Responses)
            : buildClient2026DocumentRows(client2026Responses);

        const hasAny = (key: string) =>
          rows.some((r: any) => {
            const v = String(r[key] ?? '').trim();
            return v !== '' && v !== '-';
          });

        const columnDefs = isLegacyClient2026Besoin
          ? selectLegacyClient2026DocumentColumns(rows)
          : CLIENT_2026_ALL_COL_DEFS;
        const visibleCols = columnDefs.filter((c) => c.always || hasAny(c.key));
        const headers = visibleCols.map((c) => c.label);
        const separator = headers.map(() => '---');
        const tableLines = [
          `| ${headers.join(' | ')} |`,
          `| ${separator.join(' | ')} |`,
          ...rows.map((r: any) => `| ${visibleCols.map((c) => escapeClient2026MarkdownCell(r[c.key])).join(' | ')} |`),
        ];

        const tableMarkdown = tableLines.join('\n');

        const besoinName = String((besoin as any).nom || (besoin as any).title || 'Prise de besoin - Maturité et besoins client').trim();
        const markdown = `# ${besoinName}\n\n${tableMarkdown.trim()}\n`;
        const title = `Prise de besoin - Maturité et besoins client – ${clientName}`;

        const saved = await createDocument({
          client_id: selectedClientId,
          document_type_code: documentType.code,
          besoin_id: selectedBesoinId,
          quality_level: qualityLevel,
          title,
          content: { markdown },
          status: 'draft',
          showpriseclient: false,
          presented_client: false,
          archived: false,
          metadata: {
            client2026_schema: client2026SourceSchema,
            template_version: String((besoin as any)?.template_version || (isLegacyClient2026Besoin ? '2.0.1' : client2026SourceSchema === 2 ? '3.0.0' : '3.1.0')),
            ...(!isLegacyClient2026Besoin
              ? {
                  client2026_maturity_profile: client2026SourceSchema === 2
                    ? buildClient2026V3MaturityProfile(client2026Responses)
                    : buildClient2026MaturityProfile(client2026Responses),
                }
              : {}),
          },
        });

        if (onCreated && saved.id) {
          onCreated(saved.id);
        }
        onClose();
        setGenerationProgress(100);
        setGenerationStep('Génération terminée !');
        return;
      }

      // Cas spécial : PRISME - KMI (diagnostic 6 piliers, échelle 0 → 5 par question)
      if (documentType.code === 'PRISME_KMI' || documentType.code === 'PRISME_KMI_CIBLE') {
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientName = selectedClient?.nom || 'Client';
        const kmiVariant = documentType.code === 'PRISME_KMI_CIBLE' ? 'cible' : 'actuelle';

        const kmiContext = buildKmiContextMarkdown({
          clientName,
          besoin: besoin as any,
          variant: kmiVariant,
        });

        const aiResult = await generateManagedDocumentWithAI(
          {
            documentTypeCode: documentType.code,
            documentTypeTitle: documentType.title,
            qualityLevel,
            qualityLabel: qualityDef.label,
            frameworks: documentType.frameworks,
            context: kmiContext,
            templateId: (besoin as any).template_id || undefined,
          },
          undefined
        );

        const scores = computeKmiPillarScores(besoin as any);
        const title = `${documentType.title} – ${clientName}`;

        const saved = await createDocument({
          client_id: selectedClientId,
          document_type_code: documentType.code,
          besoin_id: selectedBesoinId,
          quality_level: qualityLevel,
          title,
          content: { markdown: aiResult.content },
          status: 'draft',
          showpriseclient: false,
          presented_client: false,
          archived: false,
          metadata: {
            kmi: {
              variant: kmiVariant,
              pillars: scores.map((s) => ({
                index: s.pillarIndex,
                title: s.pillarTitle,
                averageScore: s.averageScore,
                countedAnswers: s.countedAnswers,
              })),
            },
          },
        });

        if (onCreated && saved.id) {
          onCreated(saved.id);
        }
        onClose();
        setGenerationProgress(100);
        setGenerationStep('Génération terminée !');
        return;
      }

      // Cas spécial : document narratif IA basé sur le questionnaire 2026
      if (documentType.code === 'PRISE_BESOIN_CLIENT_2026_ANALYSE') {
        const aiResult = await generateManagedDocumentWithAI(
          {
            documentTypeCode: documentType.code,
            documentTypeTitle: documentType.title,
            qualityLevel,
            qualityLabel: qualityDef.label,
            frameworks: documentType.frameworks,
            templateId: (besoin as any).template_id || undefined,
            sourceSchema: client2026SourceSchema,
            sourceVersion: String((besoin as any)?.template_version || ''),
            besoinId: selectedBesoinId!,
          },
          undefined
        );
        if (!aiResult.jobId) {
          throw new Error('La génération sécurisée du rapport n’a retourné aucun job');
        }
        const saved = await finalizeClient2026AnalysisDocument(aiResult.jobId, selectedBesoinId!);

        if (onCreated && saved.id) {
          onCreated(saved.id);
        }
        onClose();
        setGenerationProgress(100);
        setGenerationStep('Génération terminée !');
        return;
      }

      // Callback de progression pour POLITIQUE_CYBER
      const handleProgress = (progressData: { part: number; total: number; progress: number; message: string }) => {
        setGenerationProgress(progressData.progress);
        setGenerationStep(progressData.message);
      };

      const aiResult = await generateManagedDocumentWithAI(
        {
          documentTypeCode: documentType.code,
          documentTypeTitle: documentType.title,
          qualityLevel,
          qualityLabel: qualityDef.label,
          frameworks: documentType.frameworks,
          context,
          templateId: (besoin as any).template_id || undefined,
        },
        (documentType.code === 'POLITIQUE_CYBER' ||
          documentType.code === 'PLAN_DIRECTEUR' ||
          documentType.code === 'PLAN_DIRECTEUR_5_ANS_V2') ? handleProgress : undefined
      );

      const markdown = aiResult.content;

      const client = clients.find((c) => c.id === selectedClientId);
      const title = `${documentType.title} – ${client?.nom || 'Client'}`;

      const saved = await createDocument({
        client_id: selectedClientId,
        document_type_code: documentType.code,
        besoin_id: isPlanDirecteur5AnsV2
          ? planDirecteur5AnsV2Sources.find((source) => source.key === 'client2026')?.besoin?.id || null
          : selectedBesoinId || null,
        quality_level: qualityLevel,
        title,
        content: { markdown },
        status: 'draft',
        showpriseclient: false,
        presented_client: false,
        archived: false,
        metadata: isEbaucheOds
          ? {
              ebauche_ods: {
                kmi_pillars: ebaucheOdsSources.find((source) => source.key === 'kmi')?.besoin
                  ? computeKmiPillarScores(
                      ebaucheOdsSources.find((source) => source.key === 'kmi')?.besoin as any
                    )
                  : [],
              },
              sources: ebaucheOdsSources.reduce<Record<string, any>>((acc, source) => {
                acc[source.key] = source.besoin
                  ? {
                      id: source.besoin.id,
                      template_id: (source.besoin as any).template_id,
                      nom: source.besoin.nom,
                      created_at: (source.besoin as any).created_at,
                      updated_at: (source.besoin as any).updated_at,
                    }
                  : null;
                return acc;
              }, {}),
            }
          : isPlanDirecteur5AnsV2
            ? {
                plan_directeur_5_ans_v2: {
                  kmi_pillars: planDirecteur5AnsV2Sources.find((source) => source.key === 'kmi')?.besoin
                    ? computeKmiPillarScores(
                        planDirecteur5AnsV2Sources.find((source) => source.key === 'kmi')?.besoin as any
                      )
                    : [],
                },
                sources: planDirecteur5AnsV2Sources.reduce<Record<string, any>>((acc, source) => {
                  acc[source.key] = source.besoin
                    ? {
                        id: source.besoin.id,
                        template_id: (source.besoin as any).template_id,
                        nom: source.besoin.nom,
                        created_at: (source.besoin as any).created_at,
                        updated_at: (source.besoin as any).updated_at,
                      }
                    : null;
                  return acc;
                }, {}),
              }
          : {},
      });

      if (onCreated && saved.id) {
        onCreated(saved.id);
      }
      onClose();
      setGenerationProgress(100);
      setGenerationStep('Génération terminée !');
    } catch (e: any) {
      console.error('Erreur génération / création document:', e);
      setError(e?.message || 'Erreur lors de la création du document');
    } finally {
      window.clearInterval(intervalId);
      setTimeout(() => {
        setIsGenerating(false);
      }, 600);
    }
  };

  const stepTitle =
    step === 1
      ? 'Sélection du client'
      : step === 2
      ? 'Sélection de la prise de besoin'
      : 'Niveau de qualité ITIL / COBIT';

  if (isGenerating) {
    // Extraire le numéro de partie depuis generationStep si c'est POLITIQUE_CYBER
    const isPolitiqueCyber = documentType?.code === 'POLITIQUE_CYBER';
    const isPlanDirecteurMultiPart =
      documentType?.code === 'PLAN_DIRECTEUR' || documentType?.code === 'PLAN_DIRECTEUR_5_ANS_V2';
    const partMatch = generationStep?.match(/Partie (\d+)\/(\d+)/);
    const currentPart = partMatch ? parseInt(partMatch[1]) : undefined;
    const totalParts = partMatch ? parseInt(partMatch[2]) : undefined;

    return (
      <GenerationProgress
        currentStep={generationStep || 'Préparation du document...'}
        progress={generationProgress}
        documentLabels={{
          doc1: documentType?.title || 'Document',
          doc2: 'Mise en forme IA',
        }}
        isMultiPart={isPolitiqueCyber || isPlanDirecteurMultiPart}
        currentPart={currentPart}
        totalParts={totalParts}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-bz-blue via-emerald-600 to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Créer un document – {documentType.title}</h2>
            <p className="text-[11px] text-white/80">
              Étape {step} sur 3 • {stepTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-xs text-gray-700 bg-gray-50">
          {error && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[11px] text-red-700">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
              <p className="text-[11px] text-gray-600">
                Sélectionne le <strong>client</strong> pour lequel tu veux générer ce document. Seuls
                les clients actifs sont affichés.
              </p>
              {isLoadingClients ? (
                <p className="text-xs text-gray-500">Chargement des clients...</p>
              ) : (
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                    Client
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) =>
                      setSelectedClientId(e.target.value ? parseInt(e.target.value, 10) : '')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="">Sélectionnez un client...</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nom}
                        {c.secteur ? ` (${c.secteur})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
              <p className="text-[11px] text-gray-600">
                {isEbaucheOds ? (
                  <>
                    PRIsME utilise automatiquement les <strong>dernières sources disponibles</strong> pour
                    ce client: ODS, Client 2026 et KMI.
                  </>
                ) : isPlanDirecteur5AnsV2 ? (
                  <>
                    PRIsME utilise automatiquement les <strong>dernières sources disponibles</strong> pour
                    ce client: KMI pour la maturité actuelle et Client 2026 pour le contenu du plan.
                  </>
                ) : (
                  <>
                    Choisis la <strong>prise de besoin</strong> de référence. On affiche uniquement celles
                    qui sont cohérentes avec ce type de document.
                  </>
                )}
              </p>
              {(!selectedClientId || typeof selectedClientId !== 'number') && (
                <p className="text-[11px] text-gray-500">
                  Sélectionne d&apos;abord un client à l&apos;étape précédente.
                </p>
              )}
              {selectedClientId && typeof selectedClientId === 'number' && (
                <>
                  {isLoadingBesoins ? (
                    <p className="text-xs text-gray-500">Chargement des prises de besoin...</p>
                  ) : besoins.length === 0 ? (
                    <p className="text-[11px] text-gray-500">
                      Aucune prise de besoin compatible trouvée pour ce client et ce type de document.
                    </p>
                  ) : isEbaucheOds || isPlanDirecteur5AnsV2 ? (
                    <div className="space-y-2">
                      {(isEbaucheOds ? ebaucheOdsSources : planDirecteur5AnsV2Sources).map((source) => (
                        <div
                          key={source.key}
                          className={`px-3 py-2 rounded-lg border text-[11px] ${
                            source.besoin
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-red-200 bg-red-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-900">{source.label}</span>
                            <span
                              className={`text-[10px] font-semibold ${
                                source.besoin ? 'text-emerald-700' : 'text-red-700'
                              }`}
                            >
                              {source.besoin ? 'Trouvé' : 'Manquant'}
                            </span>
                          </div>
                          {source.besoin ? (
                            <p className="text-[10px] text-gray-600 mt-0.5">
                              {source.besoin.nom || 'Sans nom'} •{' '}
                              {(source.besoin as any).updated_at || (source.besoin as any).created_at
                                ? new Date(
                                    ((source.besoin as any).updated_at || (source.besoin as any).created_at) as string
                                  ).toLocaleDateString('fr-CA')
                                : 'Date inconnue'}
                            </p>
                          ) : (
                            <p className="text-[10px] text-red-700 mt-0.5">
                              Ce formulaire doit exister pour générer {isEbaucheOds ? "l'Ébauche d'ODS" : 'le Plan Directeur 5 ans V2'}.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {besoins.map((b: any) => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => setSelectedBesoinId(b.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg border text-[11px] ${
                            selectedBesoinId === b.id
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-gray-900">
                              {b.nom || 'Sans nom'}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {b.template_id} •{' '}
                              {b.created_at
                                ? new Date(b.created_at).toLocaleDateString('fr-CA')
                                : ''}
                            </span>
                          </div>
                          {b.description && (
                            <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">
                              {b.description}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
              <p className="text-[11px] text-gray-600">
                Choisis le <strong>niveau de qualité</strong> du document. Plus le niveau est élevé,
                plus le document sera détaillé, structuré et exécutif.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                {QUALITY_LEVELS.map((q) => (
                  <button
                    key={q.level}
                    type="button"
                    onClick={() => setQualityLevel(q.level)}
                    className={`px-3 py-2 rounded-lg border text-left text-[11px] ${
                      qualityLevel === q.level
                        ? 'border-bz-blue bg-bz-blue/5'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">
                      Niveau {q.level} – {q.shortLabel}
                    </p>
                    <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-3">
                      {q.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between text-xs">
          <button
            onClick={step === 1 ? onClose : goPrev}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {step === 1 ? (
              <>
                <X className="w-4 h-4" />
                Annuler
              </>
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                Étape précédente
              </>
            )}
          </button>
          <button
            onClick={step === 3 ? handleCreate : goNext}
            disabled={isGenerating}
            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-bz-blue text-white font-semibold hover:bg-bz-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {step === 3 ? (
              <>
                {isGenerating ? 'Génération en cours...' : 'Créer le document'}
              </>
            ) : (
              <>
                Étape suivante
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
